/**
 * Conversation Detailed Report — the Analytics view.
 *
 * Derived from the same rows the table renders, so the two tabs are two
 * readings of one result set rather than two queries that can drift.
 *
 * Rows here are `Record<string, unknown>` (the column catalog is partly
 * tenant-defined), so every read goes through a narrowing helper rather than a
 * cast — a tenant whose `csatScore` arrives as a string must not turn a KPI
 * into `NaN`.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import {
  AlertTriangle, CalendarClock, Clock, Gauge, Inbox, Layers, MessageSquare,
  ShieldCheck, Users,
} from 'lucide-react'

import {
  AnalyticsCanvas,
  AttentionList,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  Heatmap,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
  type AttentionItem,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  WEEKDAYS,
  barDataset,
  barOptions,
  breachBy,
  dailySeries,
  durationBuckets,
  formatCount,
  formatPct,
  formatSeconds,
  hourDayMatrix,
  hourLabel,
  lineDataset,
  lineOptions,
  meanOf,
  medianOf,
  ratePct,
  shortDay,
  tally,
} from '../analytics/viz.js'

type Row = Record<string, unknown>

/** Numeric read that tolerates numeric strings and rejects everything else. */
function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/** Only an explicit `true` counts as a breach — null means "not measured". */
function isTrue(value: unknown): boolean {
  return value === true || value === 'true'
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== ''
}

const LIST_LIMIT = 8
/** Breach rate past this is called out in the drivers panel. */
const BREACH_ALERT = 10

export default function ConversationDetailAnalytics({
  rows,
}: {
  rows: readonly Row[]
}): JSX.Element {
  const stats = useMemo(() => {
    // SLA/OLA rates count only rows the flag was actually evaluated on;
    // dividing by every row would quietly report a better number than reality.
    const slaMeasured = rows.filter((r) => hasValue(r['slaBreached']))
    const olaMeasured = rows.filter((r) => hasValue(r['olaBreached']))
    const slaBreached = slaMeasured.filter((r) => isTrue(r['slaBreached'])).length
    const olaBreached = olaMeasured.filter((r) => isTrue(r['olaBreached'])).length
    const csat = rows.map((r) => num(r['csatScore'])).filter((v): v is number => v !== null)
    const responseTimes = rows.map((r) => num(r['actualResponseTimeSec']))
    const resolutionTimes = rows.map((r) => num(r['actualResolutionTimeSec']))

    return {
      total: rows.length,
      slaMeasured: slaMeasured.length,
      slaBreached,
      slaWithin: slaMeasured.length - slaBreached,
      slaBreachPct: ratePct(slaBreached, slaMeasured.length),
      olaMeasured: olaMeasured.length,
      olaBreached,
      olaWithin: olaMeasured.length - olaBreached,
      olaBreachPct: ratePct(olaBreached, olaMeasured.length),
      responseTimes,
      resolutionTimes,
      avgResponse: meanOf(responseTimes),
      medianResponse: medianOf(responseTimes),
      avgResolution: meanOf(resolutionTimes),
      medianResolution: medianOf(resolutionTimes),
      csatAvg: meanOf(csat),
      csatCount: csat.length,
      resolved: rows.filter((r) => hasValue(r['resolvedAt'])).length,
      closed: rows.filter((r) => hasValue(r['closedAt'])).length,
      unanswered: rows.filter((r) => !hasValue(r['firstRespondedAt'])).length,
    }
  }, [rows])

  const volume = useMemo(() => dailySeries(rows, 'openedAt'), [rows])
  const volumeData = useMemo(
    () => ({
      labels: volume.labels,
      datasets: [lineDataset('Conversations created', volume.values, VIZ.series[0], { fill: true })],
    }),
    [volume],
  )
  const busiestDay = useMemo(() => {
    let best: { label: string; value: number } | null = null
    volume.labels.forEach((label, i) => {
      const value = volume.values[i] ?? 0
      if (!best || value > best.value) best = { label, value }
    })
    return best as { label: string; value: number } | null
  }, [volume])

  const heat = useMemo(() => hourDayMatrix(rows, 'openedAt'), [rows])

  // ── Response-time distribution ─────────────────────────────────────────────
  const distribution = useMemo(() => durationBuckets(stats.responseTimes), [stats.responseTimes])
  const distributionData = useMemo(
    () => ({
      labels: distribution.map((b) => b.label),
      // One series, one hue: bar length already encodes magnitude, so tinting
      // by size would double-encode it.
      datasets: [barDataset('Conversations', distribution.map((b) => b.value), VIZ.series[0])],
    }),
    [distribution],
  )
  const distributionTotal = distribution.reduce((sum, b) => sum + b.value, 0)

  // ── Breach drivers ─────────────────────────────────────────────────────────
  const drivers = useMemo((): AttentionItem[] => {
    const byPriority = breachBy(rows, 'priority', 'slaBreached', { limit: 4, minSample: 3 })
    const byCategory = breachBy(rows, 'category', 'slaBreached', { limit: 4, minSample: 3 })
    return [...byPriority.map((r) => ({ ...r, kind: 'Priority' })), ...byCategory.map((r) => ({ ...r, kind: 'Category' }))]
      .filter((r) => r.pct > 0)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 8)
      .map((r) => ({
        id: `${r.kind}-${r.label}`,
        title: r.label,
        detail: `${r.kind} · ${formatCount(r.breached)} breached of ${formatCount(r.total)}`,
        value: formatPct(r.pct, 1),
        tone: r.pct > 25 ? ('critical' as const) : ('warning' as const),
      }))
  }, [rows])

  const toItems = (key: string, limit = LIST_LIMIT): BarListItem[] =>
    tally(rows, key, { limit }).map((slice) => ({
      label: slice.label,
      value: slice.value,
      meta: formatPct(ratePct(slice.value, rows.length), 1),
    }))

  const byStatus = useMemo(() => toItems('status'), [rows])
  const byChannel = useMemo(() => toItems('channelOrigin'), [rows])
  const byPriority = useMemo(() => toItems('priority'), [rows])
  const byCategory = useMemo(() => toItems('category'), [rows])
  const byAgent = useMemo(() => toItems('agentName', 10), [rows])
  const byTeam = useMemo(() => toItems('teamName'), [rows])

  if (rows.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            The current filters returned no conversations. Widen the date range or clear a filter,
            then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const peak = heat.peak

  return (
    <AnalyticsCanvas>
      {/* ── The one number the view leads with ── */}
      <HeroBand
        eyebrow="Conversations in scope"
        value={formatCount(stats.total)}
        caption={
          volume.labels.length > 0
            ? `Created between ${shortDay(volume.labels[0] ?? '')} and ${shortDay(volume.labels[volume.labels.length - 1] ?? '')}`
            : 'Across the filtered window'
        }
        spark={volume.values}
        Icon={Inbox}
        facts={[
          { label: 'Resolved', value: formatCount(stats.resolved) },
          {
            label: 'SLA breached',
            value: formatPct(stats.slaBreachPct, 1),
            tone: breachTone(stats.slaBreachPct),
          },
          { label: 'Median response', value: formatSeconds(stats.medianResponse) },
          { label: 'Median resolution', value: formatSeconds(stats.medianResolution) },
        ]}
      />

      {/* ── Supporting numbers ── */}
      <KpiRow>
        <StatTile
          label="Avg first response"
          value={formatSeconds(stats.avgResponse)}
          hint={`Median ${formatSeconds(stats.medianResponse)}`}
        />
        <StatTile
          label="Avg resolution"
          value={formatSeconds(stats.avgResolution)}
          hint={`Median ${formatSeconds(stats.medianResolution)}`}
        />
        <StatTile
          label="Awaiting first reply"
          value={formatCount(stats.unanswered)}
          hint={`${formatPct(ratePct(stats.unanswered, stats.total), 1)} of the result set`}
          tone={stats.unanswered > 0 ? 'warning' : 'good'}
        />
        <StatTile
          label="OLA breached"
          value={formatPct(stats.olaBreachPct, 1)}
          tone={breachTone(stats.olaBreachPct)}
          hint={`${formatCount(stats.olaBreached)} of ${formatCount(stats.olaMeasured)} measured`}
        />
        <StatTile
          label="Closed"
          value={formatCount(stats.closed)}
          hint={`${formatPct(ratePct(stats.closed, stats.total), 1)} of the result set`}
        />
        <StatTile
          label="Avg CSAT"
          value={stats.csatAvg === null ? '—' : stats.csatAvg.toFixed(2)}
          hint={stats.csatCount > 0 ? `${formatCount(stats.csatCount)} scored` : 'No survey responses'}
        />
      </KpiRow>

      {/* ── When the work arrives ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${volume.labels.length} day${volume.labels.length === 1 ? '' : 's'}`}>
          Arrival pattern
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Conversations created"
            subtitle="Per day, across the filtered window"
            Icon={CalendarClock}
            wide
            height={280}
            footnote={
              busiestDay
                ? `Busiest day: ${shortDay(busiestDay.label)} with ${formatCount(busiestDay.value)} conversations.`
                : undefined
            }
          >
            {volume.labels.length === 0 ? (
              <CardEmpty message="No conversation in this result set has a creation date." />
            ) : (
              <Chart
                type="line"
                data={volumeData}
                options={lineOptions({ tickLabel: shortDay })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="When conversations arrive"
            subtitle="Weekday by hour of day, in your local time"
            Icon={Clock}
            wide
            height={280}
            footnote={
              peak
                ? `Peak hour: ${WEEKDAYS[peak.day]} at ${hourLabel(peak.hour)} — ${formatCount(peak.value)} conversations. Staff to the dark cells, not to the daily average.`
                : undefined
            }
          >
            <Heatmap matrix={heat.matrix} max={heat.max} />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Service levels ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Service levels</SectionLabel>
        <CardGrid>
          <ChartCard
            title="First response time"
            subtitle="How long customers waited for a first reply"
            Icon={MessageSquare}
            footnote={
              distributionTotal > 0
                ? `${formatCount(distributionTotal)} conversations have a measured first response; the median is ${formatSeconds(stats.medianResponse)}.`
                : undefined
            }
          >
            {distributionTotal === 0 ? (
              <CardEmpty message="No conversation in this window has a measured first response." />
            ) : (
              <Chart
                type="bar"
                data={distributionData}
                options={barOptions({ horizontal: false })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Breach drivers"
            subtitle="Priorities and categories with the highest SLA breach rate"
            Icon={AlertTriangle}
          >
            <AttentionList
              items={drivers}
              emptyMessage={`No priority or category is breaching more than ${BREACH_ALERT}% of its conversations.`}
            />
          </ChartCard>

          <ChartCard title="SLA outcome" subtitle="Conversations with an SLA evaluated" Icon={ShieldCheck} height={220}>
            <Meter
              segments={[
                { label: 'Within SLA', value: stats.slaWithin, color: VIZ.status.good },
                { label: 'Breached', value: stats.slaBreached, color: VIZ.status.critical },
              ]}
              emptyMessage="No conversation in this window had an SLA evaluated."
            />
          </ChartCard>

          <ChartCard title="OLA outcome" subtitle="Conversations with an OLA evaluated" Icon={Gauge} height={220}>
            <Meter
              segments={[
                { label: 'Within OLA', value: stats.olaWithin, color: VIZ.status.good },
                { label: 'Breached', value: stats.olaBreached, color: VIZ.status.critical },
              ]}
              emptyMessage="No conversation in this window had an OLA evaluated."
            />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Composition ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Composition</SectionLabel>
        <CardGrid>
          <ChartCard title="By status" subtitle="Share of the filtered conversations" Icon={Layers}>
            <BarList items={byStatus} format={formatCount} />
          </ChartCard>
          <ChartCard title="By channel" subtitle="Where the conversation came in" Icon={MessageSquare}>
            <BarList items={byChannel} format={formatCount} />
          </ChartCard>
          <ChartCard title="By priority" subtitle="As classified on the conversation" Icon={Gauge}>
            <BarList items={byPriority} format={formatCount} />
          </ChartCard>
          <ChartCard title="Top categories" subtitle="Tail folded into “Other”" Icon={Layers}>
            <BarList items={byCategory} format={formatCount} />
          </ChartCard>
          <ChartCard title="Busiest agents" subtitle="By conversations in this result set" Icon={Users}>
            <BarList items={byAgent} format={formatCount} />
          </ChartCard>
          <ChartCard title="By team" subtitle="Ownership across the filtered window" Icon={Users}>
            <BarList items={byTeam} format={formatCount} />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}

/** Breach-rate tone: 0–5% good, 5–15% warning, worse than that critical. */
function breachTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct <= 5) return 'good'
  if (pct <= 15) return 'warning'
  return 'critical'
}
